/**
 * Payroll-verification funnel telemetry.
 *
 * The pay-stub upload fallback exists to rescue applicants who tried to
 * connect a payroll provider and couldn't. Whether it actually rescues them is
 * an empirical question, so the four funnel steps are instrumented:
 *
 *   connect-attempt → exit-without-success → fallback-shown → upload-complete
 *
 * Read it as a ladder: the gap between `exit-without-success` and
 * `upload-complete` is the drop-off the fallback is meant to recover. If
 * `fallback-shown` fires often but `upload-complete` rarely follows, the
 * fallback isn't earning its per-verification cost.
 *
 * Transport is Microsoft Clarity custom events — the analytics surface this
 * app already has (see `src/lib/clarity.ts`). That makes it **consent-gated
 * for free**: Clarity is never injected without accepted analytics cookies, so
 * a declining applicant emits nothing.
 *
 * Events carry NO applicant data — no session token, email, employer, or
 * amount. Just the step name and which flow it belongs to. Clarity's public
 * cookie policy promises no PII (`docs/microsoft-clarity-and-cookie-policy.md`)
 * and the dev constitution §4 forbids PII in logs; an event name is enough to
 * count a funnel.
 */

export type PayrollFunnelStep =
  /** Applicant clicked a connect/upload CTA — the top of the funnel. */
  | "payroll_connect_attempt"
  /** Plaid Link closed without a success callback — the UC1 population. */
  | "payroll_exit_without_success"
  /** The upload fallback (State B) became visible to the applicant. */
  | "payroll_fallback_shown"
  /** Plaid Link reported a successful pay-stub upload (parsing still pending). */
  | "payroll_upload_complete";

/** Which flow the event belongs to. Absent for flow-agnostic steps. */
export type PayrollFunnelFlow = "digital" | "document";

type ClarityFn = (command: "event", name: string) => void;

/**
 * Emit one funnel step. Silent no-op when Clarity isn't loaded — which is the
 * normal case for an applicant who declined analytics cookies, and also in
 * tests and SSR. Never throws: telemetry must not be able to break the
 * verification flow.
 */
export function trackPayrollFunnel(step: PayrollFunnelStep, flow?: PayrollFunnelFlow): void {
  if (typeof window === "undefined") return;
  const clarity = (window as unknown as { clarity?: ClarityFn }).clarity;
  if (typeof clarity !== "function") return;

  try {
    clarity("event", flow ? `${step}__${flow}` : step);
  } catch {
    /* analytics must never break the flow */
  }
}
